<script setup lang="ts">

import { usePresentations } from '@presentation/Types/Presentations';
import { DefaultSections, usePresentation } from "@presentation/Types";

const presentation = usePresentation();
const presentations = usePresentations();

</script>

<template>

    <div class="absolute w-full top-0">

        <div class="absolute w-full h-20"
            :style="{ 
                background: `linear-gradient( to bottom, rgb( from ${presentation.theme.bgColorFrom} r g b / 95%), rgb( from ${presentation.theme.bgColorFrom} r g b / 80%) 80%, transparent )`
            }"
        />
        
        <div class="max-w-[2160px] mx-auto flex items-center justify-center px-2 md:px-6 h-28">

            <!-- SECTION TITLE -->
            <transition mode="out-in"
                enter-active-class="transition ease-out duration-150"
                enter-from-class="transform opacity-0 scale-95 translate-y-20"
                enter-to-class="transform opacity-100 scale-100 translate-y-0"
                leave-active-class="transition ease-in duration-150"
                leave-from-class="transform opacity-100 scale-100 translate-y-0"
                leave-to-class="transform opacity-0 scale-95 translate-y-20">
                
                <div v-if="presentation.displayPageTitle && presentation.isTransitioning == false && presentation.currentPage!.hideTitle !== true && presentation.isTitleComponent == false" 
                    :key="presentation.currentPage!.title"
                    class="
                        absolute top-7 w-full 
                        flex flex-col justify-evenly items-center gap-px
                        max-w-[90%] lg:max-w-[80%] xl:max-w-[60%]">
                    <h1 class="
                        px-4 py-2 top-0 text-center
                        gap-2 justify-center items-center
                        text-2xl 3xl:text-3xl 4xl:text-4xl
                        font-semibold uppercase"
                        :style="{ color: presentation.theme.headerColor }">
                        {{ presentation.currentPage!.title }}
                    </h1>
                </div>

            </transition>

            <div class="absolute left-auto md:left-6 flex items-center justify-center gap-4 2xl:scale-110 2xl:left-8">
                <button 
                    title="Restart the presentation"
                    @click="() => presentation.goto( DefaultSections.Start )"
                    class="hover:brightness-110 hover:bg-white/[4%] rounded-full w-11 h-11 flex items-center justify-center hover:shadow tracking-wider">
                    <Icon name="home" />
                </button>
                <button 
                    v-if="presentation.active != DefaultSections.SlideSorter"
                    title="Open Slide Deck"
                    @click="() => presentation.goto( DefaultSections.SlideSorter )"
                    class="hover:brightness-110 hover:bg-white/[4%] rounded-full w-11 h-11 flex items-center justify-center hover:shadow tracking-wider">
                    <Icon name="grid" />
                </button>
                <button 
                    v-if="presentation.active != DefaultSections.SlideSorter"
                    title="Back"
                    @click="() => presentation.back()"
                    class="hover:brightness-110 hover:bg-white/[4%] rounded-full w-11 h-11 flex items-center justify-center hover:shadow tracking-wider">
                    <Icon name="back" />
                </button>
                <!-- <button 
                    v-if="presentation.active != DefaultSections.SlideSorter"
                    title="Toggle Fullscreen"
                    @click="() => presentation.toggleFullscreen()"
                    class="hover:brightness-110 hover:bg-white/[4%] rounded-full w-11 h-11 flex items-center justify-center hover:shadow tracking-wider">
                    <Icon name="fullscreen" />
                </button> -->

                <v-menu location="bottom"
                    v-if="presentation.active == DefaultSections.SlideSorter">
                    <template v-slot:activator="{ props, isActive }">
                        <div class="flex items-center gap-2">
                            <span class="text-sm">{{ presentation.title }}</span>
                            <span>...</span>
                            <button 
                                v-bind="props"
                                title="Switch Presentation"
                                class="hover:bg-white/[4%] rounded-full w-11 h-11 flex items-center justify-center hover:shadow tracking-wider">
                                <Icon name="presentation" />
                            </button>
                        </div>
                    </template>

                    <v-list class="p-0 !rounded-md">
                        <template 
                            v-for="item in presentations"
                            :key="item.title">
                            <v-list-item 
                                :value="item.title"
                                density="comfortable"
                                class="bg-white"
                                :to="item.path">
                                <v-list-item-title class="font-semibold">
                                    {{ item.title }}
                                </v-list-item-title>
                            </v-list-item>
                        </template>
                    </v-list>
                </v-menu> 

                <v-menu location="bottom"
                    v-if="presentation.active != DefaultSections.SlideSorter">
                    <template v-slot:activator="{ props, isActive }">
                        <div class="flex items-center gap-2">
                            <button 
                                v-bind="props"
                                title="Switch Presentation"
                                class="hover:bg-white/[4%] rounded-full w-11 h-11 flex items-center justify-center hover:shadow tracking-wider">
                                <Icon name="fullscreen" />
                            </button>
                        </div>
                    </template>

                    <v-list class="p-0 !rounded-md">
                        <v-list-item 
                            density="comfortable"
                            class="bg-white"
                            @click="() => presentation.toggleFullscreen()">
                            <v-list-item-title class="font-semibold">
                                Toggle Full Screen
                            </v-list-item-title>
                        </v-list-item>
                        <v-list-item 
                            density="comfortable"
                            class="bg-white"
                            @click="() => presentation.toggleTabletMode()">
                            <v-list-item-title class="font-semibold">
                                Toggle Tablet Mode
                            </v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu> 

            </div>

            <Image 
                :src="presentation.theme.logo" 
                class="hidden md:block h-8 lg:h-14 absolute right-6 translate-y-0.5 2xl:scale-110 2xl:right-10 2xl:translate-y-2" 
                :class="presentation.theme.logoClass"
            />
            
        </div>

        
    </div>

</template>